import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import SearchIcon from "@mui/icons-material/Search";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { basicURL } from "../../apiManager/APIConfig";
import {
  AddCommentCall,
  CreateQuestionCall,
  DeleteCommentCall,
  GetAllQuestionsCall,
  GetQuestionDetailsCall,
} from "../../redux/api/question.api";
import { GetPublicCall } from "../../redux/api/public.api";
import { useSelector } from "react-redux";

const PAGE_SIZE = 12;
const COMMUNITY_ADS_PUBLIC_KEY = "communityShowAds";

function getAuthorLabel(item) {
  if (!item?.user) return "User";
  return `${item.user.first_name || ""} ${item.user.last_name || ""}`.trim() || "User";
}

function toAbsoluteUrl(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(basicURL || "").replace(/\/+$/, "");
  const path = s.replace(/^\/+/, "");
  return `${base}/${path}`;
}

function getQuestionFiles(item) {
  const raw = item?.files ?? item?.Files ?? [];
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

function isVideoFile(f) {
  const mime = String(f?.type || f?.mime || "").toLowerCase();
  const ref = `${f?.name || ""} ${f?.alias || ""} ${f?.url || ""} ${f?.path || ""}`.toLowerCase();
  return (
    mime.startsWith("video/") ||
    /\.(mp4|mov|m4v|webm)(\?|$|#)/i.test(ref)
  );
}

function getMediaInfo(item) {
  const allFiles = getQuestionFiles(item);
  const fileVideo = allFiles.find(isVideoFile);
  const rootVideo = item?.video_url || item?.videoUrl || item?.uploaded_file?.url || "";
  const videoUrl = fileVideo?.url || rootVideo || "";

  if (videoUrl) {
    const nonVideo = allFiles.filter((f) => f?.url && !isVideoFile(f));
    const posterCandidate =
      item?.thumbnail_url ||
      item?.thumbnailUrl ||
      item?.picture_url ||
      nonVideo?.[0]?.url ||
      "";
    return {
      url: toAbsoluteUrl(videoUrl),
      posterUrl: toAbsoluteUrl(posterCandidate),
      isVideo: true,
    };
  }

  const imageCandidate =
    item?.image_url ||
    item?.imageUrl ||
    item?.picture_url ||
    item?.uploaded_file?.url ||
    allFiles.find((f) => f?.url)?.url ||
    "";
  return { url: toAbsoluteUrl(imageCandidate), posterUrl: "", isVideo: false };
}

function CommunityCard({ item, onOpenComments }) {
  const body = item?.body || item?.first_user_message || "—";
  const { url: mediaUrl, posterUrl, isVideo } = getMediaInfo(item);
  const created = item?.createdAt || item?.created_at || "";
  const author = getAuthorLabel(item);

  return (
    <Card variant="outlined" sx={{ p: 1.4, borderRadius: 2.5, height: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <Avatar sx={{ width: 34, height: 34, flexShrink: 0 }}>
            {author.charAt(0)}
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {author}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {created}
            </Typography>
          </Box>
        </Stack>
        <Chip size="small" label="Q" />
      </Stack>

      {mediaUrl ? (
        <Box mt={1.1}>
          {isVideo ? (
            <Box
              component="video"
              controls
              preload="metadata"
              src={mediaUrl}
              poster={posterUrl || undefined}
              sx={{
                width: "100%",
                borderRadius: 1.5,
                border: "1px solid rgba(148,163,184,.35)",
                backgroundColor: "#000",
              }}
            />
          ) : (
            <Box
              component="img"
              src={mediaUrl}
              alt="question media"
              sx={{
                width: "100%",
                borderRadius: 1.5,
                border: "1px solid rgba(148,163,184,.35)",
                objectFit: "cover",
                maxHeight: 220,
              }}
            />
          )}
        </Box>
      ) : null}

      <Typography variant="body2" sx={{ mt: 1.1, whiteSpace: "pre-wrap" }}>
        {body}
      </Typography>

      <Stack direction="row" spacing={2} mt={1.2}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => onOpenComments?.(item)}
        >
          {item?.commentCount || 0} comments
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {item?.viewCount || 0} views
        </Typography>
      </Stack>
    </Card>
  );
}

function CommentsDialog({
  open,
  onClose,
  question,
  me,
  onRefresh,
}) {
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const comments = Array.isArray(question?.comments) ? question.comments : [];

  const mentionCandidates = useMemo(() => {
    const map = new Map();
    if (question?.user?.id) map.set(question.user.id, question.user);
    comments.forEach((c) => {
      if (c?.user?.id) map.set(c.user.id, c.user);
    });
    return Array.from(map.values());
  }, [question, comments]);

  const mentionIds = useMemo(() => {
    const found = [];
    const text = commentText || "";
    mentionCandidates.forEach((u) => {
      const full = `${u?.first_name || ""} ${u?.last_name || ""}`.trim();
      const username = full || u?.email || "";
      if (!username) return;
      if (text.toLowerCase().includes(`@${username.toLowerCase()}`)) {
        found.push(u.id);
      }
    });
    return Array.from(new Set(found));
  }, [commentText, mentionCandidates]);

  const canDelete = (commentUserId) =>
    String(me?.role || "").toLowerCase() === "admin" || String(me?.id || "") === String(commentUserId || "");

  const submitComment = async () => {
    if (!question?.id || !commentText.trim() || submitting) return;
    setSubmitting(true);
    const res = await AddCommentCall({
      question_id: question.id,
      comment: commentText.trim(),
      mentioned_users: mentionIds,
    });
    setSubmitting(false);
    if (res?.status === 200 || res?.status === 201) {
      setCommentText("");
      await onRefresh?.();
    }
  };

  const deleteComment = async (commentId) => {
    const res = await DeleteCommentCall(commentId);
    if (res?.status === 200 || res?.status === 204) {
      await onRefresh?.();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <QuestionAnswerOutlinedIcon fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Comments
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.2, whiteSpace: "pre-wrap" }}>
          {question?.body || "—"}
        </Typography>
        <List dense sx={{ mb: 1, maxHeight: 280, overflowY: "auto" }}>
          {comments.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No comments yet.
            </Typography>
          ) : (
            comments.map((c) => (
              <ListItem
                key={c.id}
                secondaryAction={
                  canDelete(c?.user?.id) ? (
                    <IconButton edge="end" onClick={() => deleteComment(c.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
                sx={{ px: 0.3 }}
              >
                <ListItemText
                  primary={`${c?.user?.first_name || ""} ${c?.user?.last_name || ""}`.trim() || "User"}
                  secondary={c?.comment || ""}
                  primaryTypographyProps={{ variant: "subtitle2", fontWeight: 700 }}
                  secondaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            ))
          )}
        </List>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Write a comment… use @Name to mention"
        />
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Close
        </MDButton>
        <MDButton color="info" disabled={!commentText.trim() || submitting} onClick={submitComment}>
          {submitting ? "Posting..." : "Post comment"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function CommunityPage() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const [tab, setTab] = useState("community");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [communityRows, setCommunityRows] = useState([]);
  const [universityRows, setUniversityRows] = useState([]);
  const [askOpen, setAskOpen] = useState(false);
  const [askBody, setAskBody] = useState("");
  const [askFile, setAskFile] = useState(null);
  const [page, setPage] = useState(1);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showPromotions, setShowPromotions] = useState(() => {
    const raw = String(localStorage.getItem("communityShowAds") ?? "true").toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes";
  });
  const me = useSelector((s) => s?.auth || {});
  const universityId =
    me?.university_id ?? me?.universityInfo?.id ?? me?.universityInfo?.university_id ?? null;
  const hasUniversityMembership = Boolean(universityId);
  const isPlatformAdmin = String(me?.role || "").toLowerCase() === "admin";
  const universityName = useMemo(
    () =>
      String(
        me?.universityInfo?.name ||
          me?.universityInfo?.short_name ||
          me?.university ||
          "University"
      ).trim() || "University",
    [me?.university, me?.universityInfo?.name, me?.universityInfo?.short_name]
  );
  const universityTabLabel = `${universityName} Private Section`;
  const isUniversityTab = tab === "university";

  useEffect(() => {
    if (!hasUniversityMembership && tab === "university") {
      setTab("community");
    }
  }, [hasUniversityMembership, tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      let effectiveShowPromotions = showPromotions;
      try {
        const publicRes = await GetPublicCall();
        const rows = Array.isArray(publicRes?.data) ? publicRes.data : [];
        const prefRow = rows.find((r) => r?.key === COMMUNITY_ADS_PUBLIC_KEY);
        if (prefRow) {
          const raw = String(prefRow?.data ?? "").toLowerCase();
          effectiveShowPromotions = raw === "true" || raw === "1" || raw === "yes";
          setShowPromotions(effectiveShowPromotions);
          localStorage.setItem("communityShowAds", String(effectiveShowPromotions));
        }
      } catch {
        // Keep local preference when public settings cannot be fetched.
      }

      const communityPromise = GetAllQuestionsCall({
        ads: effectiveShowPromotions,
        limit: 50,
        page: 1,
      });
      const universityPromise = hasUniversityMembership
        ? GetAllQuestionsCall({
            ads: false,
            limit: 50,
            page: 1,
            audience: "university",
            ...(isPlatformAdmin && universityId ? { university_id: universityId } : {}),
          })
        : Promise.resolve({ questions: [] });

      const [allRes, uniRes] = await Promise.all([communityPromise, universityPromise]);
      const incomingCommunityRows = Array.isArray(allRes?.questions) ? allRes.questions : [];
      // Safety guard: if ads are disabled in UI, strip any ad rows even if backend still returns them.
      const cleanedCommunityRows = effectiveShowPromotions
        ? incomingCommunityRows
        : incomingCommunityRows.filter((row) => !row?.ad);
      setCommunityRows(cleanedCommunityRows);
      setUniversityRows(Array.isArray(uniRes?.questions) ? uniRes.questions : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [showPromotions, hasUniversityMembership, universityId]);

  useEffect(() => {
    const onPreferenceChanged = (evt) => {
      const nextVal = Boolean(evt?.detail?.showAds);
      setShowPromotions(nextVal);
    };
    window.addEventListener("communityAdsPreferenceChanged", onPreferenceChanged);
    return () =>
      window.removeEventListener("communityAdsPreferenceChanged", onPreferenceChanged);
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const source = isUniversityTab ? universityRows : communityRows;
    if (!q) return source;
    return source.filter((r) => {
      const body = (r?.body || r?.first_user_message || "").toLowerCase();
      const user = `${r?.user?.first_name || ""} ${r?.user?.last_name || ""}`.toLowerCase();
      return body.includes(q) || user.includes(q);
    });
  }, [isUniversityTab, communityRows, universityRows, search]);

  useEffect(() => {
    setPage(1);
  }, [tab, search]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  const gridCols = isLgUp ? 3 : isMdUp ? 2 : 1;

  const openComments = async (item) => {
    if (!item?.id) return;
    const res = await GetQuestionDetailsCall(item.id);
    const q = res?.question || res?.data || null;
    if (q) {
      setSelectedQuestion(q);
      setDetailsOpen(true);
    }
  };

  const refreshSelectedQuestion = async () => {
    if (!selectedQuestion?.id) return;
    const res = await GetQuestionDetailsCall(selectedQuestion.id);
    const q = res?.question || res?.data || null;
    if (q) {
      setSelectedQuestion(q);
    }
  };

  const handleAsk = async () => {
    if (!askBody.trim()) return;
    const res = await CreateQuestionCall({
      body: askBody.trim(),
      file: askFile || undefined,
      community_scope: isUniversityTab ? "university" : "public",
      audience: isUniversityTab ? "university" : "public",
      ...(isUniversityTab && isPlatformAdmin && universityId
        ? { university_id: universityId }
        : {}),
    });
    if (res?.status === 200 || res?.status === 201) {
      setAskOpen(false);
      setAskBody("");
      setAskFile(null);
      await loadData();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={2} pb={2}>
        <Card sx={{ borderRadius: 3, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" fontWeight={700}>
              Community
            </Typography>
            <MDButton color="info" onClick={() => setAskOpen(true)} startIcon={<AddIcon />}>
              Ask
            </MDButton>
          </Stack>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab value="community" label="Community" />
            {hasUniversityMembership ? (
              <Tab value="university" label={universityTabLabel} />
            ) : null}
          </Tabs>

          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Box py={5} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {isUniversityTab
                ? `No posts yet in ${universityName}. Be the first to ask.`
                : "No items found."}
            </Typography>
          ) : (
            <>
              <Grid container spacing={1.2}>
                {pagedRows.map((item, idx) => (
                  <Grid item xs={12 / gridCols} key={item?.id || item?.thread_id || idx}>
                    <Box position="relative" height="100%">
                      <CommunityCard item={item} onOpenComments={openComments} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {rows.length > PAGE_SIZE ? (
                <Stack direction="row" justifyContent="flex-end" mt={2}>
                  <Pagination
                    page={page}
                    count={pageCount}
                    color="primary"
                    shape="rounded"
                    onChange={(_, value) => setPage(value)}
                  />
                </Stack>
              ) : null}
            </>
          )}
        </Card>
      </Box>

      <Dialog open={askOpen} onClose={() => setAskOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {isUniversityTab ? `Ask ${universityName}` : "Ask Community"}
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            minRows={4}
            fullWidth
            autoFocus
            value={askBody}
            onChange={(e) => setAskBody(e.target.value)}
            placeholder="Write your question..."
            sx={{ mb: 2 }}
          />
          <MDButton
            variant="outlined"
            color="info"
            component="label"
            startIcon={<InsertDriveFileIcon />}
          >
            {askFile ? askFile.name : "Attach image/video (optional)"}
            <input
              hidden
              type="file"
              accept="image/*,video/*"
              onChange={(e) => setAskFile(e.target.files?.[0] || null)}
            />
          </MDButton>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setAskOpen(false)}>
            Cancel
          </MDButton>
          <MDButton color="info" onClick={handleAsk} disabled={!askBody.trim()}>
            Post
          </MDButton>
        </DialogActions>
      </Dialog>

      <CommentsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        question={selectedQuestion}
        me={me}
        onRefresh={refreshSelectedQuestion}
      />
    </DashboardLayout>
  );
}

export default CommunityPage;
